import { ShoppingCart, Tag, Percent, AlertTriangle, TrendingUp, Mail, Send, Loader2, Copy, Check, Pencil, X, Phone } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import BigHeadsPanel from "./BigHeadsPanel";

export const VOLUME_PRODUCT_KEYS = ["business_cards", "flyers", "brochures", "postcards", "digital_prints", "club_flyers", "rack_cards"];

export const isVolumeQuote = (quote, productsById) => {
  if (!quote || quote.is_custom_quote) return false;
  if (quote.lines && quote.lines.length > 0) return false;
  const pid = quote.product_id;
  if (!pid) return false;
  const product = productsById?.[pid];
  const idStr = String(pid).toLowerCase();
  const cat = String(product?.category || "").toLowerCase();
  const name = String(quote.product_name || product?.name || "").toLowerCase();
  return VOLUME_PRODUCT_KEYS.some((k) => idStr.includes(k) || cat.includes(k) || name.includes(k.replace("_", " ")));
};

const formatUSD = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const buildClipboardText = (quote) => {
  const lines = quote.lines || [];
  const isCombined = lines.length > 0;
  const retail = Number(quote.retail_price || 0);
  const ups = Number(quote.ups_price || 0);
  const discount = Number(quote.discount_percent ?? 40);
  const margin = Math.max(retail - ups, 0);
  const head = isCombined ? "Roswell Print Center — Combined Quote" : `Roswell Print Center — ${quote.product_name || "Quote"}`;
  const out = [head, ""];
  if (!isCombined && quote.specifications) out.push(quote.specifications, "");
  if (isCombined) {
    out.push("Items:");
    lines.forEach((line, idx) => {
      const lr = Number(line.retail_price || 0);
      const lu = Number(line.ups_price || 0);
      const tag = line.is_custom_quote ? " [CUSTOM — PM review]" : "";
      out.push(
        `  ${idx + 1}. ${line.quantity || 0} × ${line.product_name || "Item"}${tag}`
      );
      if (line.specifications) out.push(`     ${line.specifications}`);
      if (lr === 0 && lu === 0) {
        out.push(`     To be quoted by Print Manager`);
      } else {
        out.push(`     Retail ${formatUSD(lr)} · Your price ${formatUSD(lu)}`);
      }
    });
    out.push("");
  }
  out.push(`Retail Total:     ${formatUSD(retail)}`);
  out.push(`Your Price:       ${formatUSD(ups)}  (${discount}% off)`);
  out.push(`Your Margin:      ${formatUSD(margin)}`);
  if (quote.is_custom_quote && quote.custom_note) {
    out.push("", "Note: " + quote.custom_note);
  }
  out.push("", "Quote good for 30 days.");
  return out.join("\n");
};

const UpsellCard = ({ item, onClick, index }) => {
  const isNegative = (item.retail_diff ?? 0) < 0;
  const isPaper = item.type === "paper";
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full p-3 transition hover:-translate-y-0.5"
      style={{
        background: "#fff",
        border: "1px solid var(--ups-border)",
        boxShadow: "3px 3px 0 var(--ups-ink)",
      }}
      data-testid={`upsell-card-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 flex items-center justify-center"
            style={{ background: isPaper ? "var(--ups-gold)" : "var(--ups-accent)", color: isPaper ? "var(--ups-ink)" : "#fff" }}
          >
            <TrendingUp size={12} strokeWidth={2.5} />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[color:var(--ups-subtext)]">
            {isPaper ? "Paper upgrade" : "Volume upsell"}
          </span>
        </div>
        {item.retail_diff != null && (
          <span
            className="text-[11px] font-mono font-bold"
            style={{ color: isNegative ? "var(--ups-accent)" : "var(--ups-ink)" }}
          >
            {isNegative ? "" : "+"}
            {formatUSD(item.retail_diff)}
          </span>
        )}
      </div>
      <div className="font-display font-bold text-sm leading-tight mb-1">{item.label}</div>
      {item.hint && (
        <div className="text-[11px] text-[color:var(--ups-subtext)] leading-snug mb-2">{item.hint}</div>
      )}
      <div className="flex items-baseline gap-2 text-xs font-mono">
        <span className="text-[color:var(--ups-subtext)]">Retail {formatUSD(item.retail_price)}</span>
        <span className="font-bold" style={{ color: "var(--ups-accent)" }}>
          {formatUSD(item.ups_price)} you pay
        </span>
      </div>
    </button>
  );
};

const paperOptionsForLine = (line, productsById, papersById) => {
  const product = productsById?.[line.product_id];
  const ids = [];
  if (product) {
    (product.allowed_paper_ids || []).forEach((pid) => ids.push(pid));
    (product.custom_papers || []).forEach((pp) => {
      if (!ids.includes(pp.id)) ids.push(pp.id);
    });
  }
  if (line.paper_id && !ids.includes(line.paper_id)) ids.unshift(line.paper_id);
  return ids.map((pid) => {
    const lib = papersById?.[pid];
    const customPaper = (product?.custom_papers || []).find((pp) => pp.id === pid);
    const label = lib?.label || customPaper?.label || pid;
    return { id: pid, label };
  });
};

const LineEditor = ({ line, productsById, papersById, onSave, onCancel, busy }) => {
  const [qty, setQty] = useState(Number(line.quantity || 1));
  const [paperId, setPaperId] = useState(line.paper_id || "");
  const paperOpts = paperOptionsForLine(line, productsById, papersById);
  const isOffCatalog = line.product_id === "unknown";
  const submit = (e) => {
    e.preventDefault();
    const patch = {};
    if (Number(qty) !== Number(line.quantity)) patch.quantity = Number(qty);
    if (paperId && paperId !== line.paper_id) patch.paper_id = paperId;
    if (!Object.keys(patch).length) {
      onCancel();
      return;
    }
    onSave(patch);
  };
  return (
    <form
      onSubmit={submit}
      className="mt-2 p-3 rounded-sm grid gap-2 sm:grid-cols-[120px_1fr_auto] items-end"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="line-editor"
    >
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Quantity</span>
        <input
          type="number"
          min={1}
          max={100000}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          disabled={busy}
          data-testid="line-editor-qty"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">
          Paper {isOffCatalog && "(custom item — paper not editable)"}
        </span>
        <select
          value={paperId}
          onChange={(e) => setPaperId(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          disabled={busy || isOffCatalog || paperOpts.length === 0}
          data-testid="line-editor-paper"
        >
          {paperOpts.length === 0 && <option value="">(no options)</option>}
          {paperOpts.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]"
          disabled={busy}
          data-testid="line-editor-save"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Save & Re-quote"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-2 py-2 rounded-sm hover:bg-white transition"
          aria-label="Cancel edit"
          data-testid="line-editor-cancel"
        >
          <X size={14} />
        </button>
      </div>
    </form>
  );
};

const LineItemsTable = ({ lines, onRequote, productsById, papersById }) => {
  const [editingIdx, setEditingIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="mb-5" data-testid="combined-quote-lines">
      <div className="overline mb-2">Line Items ({lines.length})</div>
      <div
        className="rounded-sm overflow-hidden"
        style={{ border: "1px solid var(--ups-border)", background: "#fff" }}
      >
        {lines.map((line, idx) => {
          const lr = Number(line.retail_price || 0);
          const lu = Number(line.ups_price || 0);
          const isCustomLine = Boolean(line.is_custom_quote);
          const tbq = lr === 0 && lu === 0;
          const editable = Boolean(onRequote) && line.product_id && line.product_id !== "unknown";
          return (
            <div
              key={idx}
              className="px-3 py-3"
              style={{ borderTop: idx === 0 ? "none" : "1px solid var(--ups-border)" }}
              data-testid={`quote-line-${idx}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className="w-7 h-7 flex items-center justify-center shrink-0 font-mono text-xs font-bold"
                    style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
                  >
                    {line.quantity || 0}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm leading-tight">{line.product_name || "Item"}</div>
                    {line.specifications && (
                      <div className="text-[12px] text-[color:var(--ups-subtext)] mt-0.5 leading-snug">{line.specifications}</div>
                    )}
                    {isCustomLine && (
                      <div
                        className="inline-block mt-1.5 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                        style={{ background: "#FFB500", color: "var(--ups-ink)" }}
                        data-testid={`quote-line-custom-tag-${idx}`}
                      >
                        Custom · PM review
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  <div className="text-right font-mono text-[12px]">
                    {tbq ? (
                      <div className="font-bold" style={{ color: "#92400E" }}>To be quoted</div>
                    ) : (
                      <>
                        <div className="text-[color:var(--ups-subtext)]">Retail {formatUSD(lr)}</div>
                        <div className="font-bold" style={{ color: "var(--ups-accent)" }}>
                          {formatUSD(lu)} you pay
                        </div>
                      </>
                    )}
                  </div>
                  {editable && editingIdx !== idx && (
                    <button
                      type="button"
                      onClick={() => setEditingIdx(idx)}
                      className="p-1.5 rounded-sm transition hover:bg-[color:var(--ups-muted)]"
                      title="Edit quantity or paper"
                      aria-label={`Edit ${line.product_name}`}
                      data-testid={`edit-line-${idx}`}
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              </div>
              {editingIdx === idx && (
                <LineEditor
                  line={line}
                  productsById={productsById}
                  papersById={papersById}
                  busy={busy}
                  onCancel={() => setEditingIdx(null)}
                  onSave={async (patch) => {
                    setBusy(true);
                    try {
                      const ok = await onRequote(idx, patch);
                      if (ok !== false) setEditingIdx(null);
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PricingQuoteCard = ({
  quote,
  onOrderNow,
  onPickUpsell,
  onEmailQuote,
  onRequote,
  productsById,
  papersById,
  disabled,
  sessionId,
  bigheadsFatheads,
  onBigheadsChange,
}) => {
  const [emailingVariant, setEmailingVariant] = useState(null); // 'full' | 'retail' | null
  const [sentVariants, setSentVariants] = useState({});
  const [copied, setCopied] = useState(false);
  if (!quote) return null;
  const retail = Number(quote.retail_price || 0);
  const ups = Number(quote.ups_price || retail * 0.6);
  const savings = retail - ups;
  const discount = quote.discount_percent ?? 40;
  const upsells = quote.upsells || [];
  const isCustom = Boolean(quote.is_custom_quote);
  const lines = quote.lines || [];
  const isCombined = lines.length > 0;
  const isBigHeads = isCombined
    ? Boolean(quote.has_bigheads)
    : (quote.product_name || "").toLowerCase().includes("big head");
  const bhQty = isCombined ? Number(quote.bigheads_qty || 0) : Number(quote.quantity || 1);
  const bhList = bigheadsFatheads || [];
  const bhReady = bhList.filter(Boolean).length;
  const bhIncomplete = isBigHeads && bhReady < bhQty;

  const handleEmailClick = async (variant) => {
    if (!onEmailQuote || emailingVariant) return;
    setEmailingVariant(variant);
    try {
      const ok = await onEmailQuote(quote, variant);
      if (ok) setSentVariants((prev) => ({ ...prev, [variant]: true }));
    } finally {
      setEmailingVariant(null);
    }
  };

  const handleCopyClick = async () => {
    const text = buildClipboardText(quote);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Quote copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy — your browser blocked clipboard access.");
    }
  };

  return (
    <div className="card-quote rounded-md p-6 sm:p-7 relative overflow-hidden" data-testid="pricing-quote-card">
      {/* Ribbon */}
      <div
        className="absolute top-0 right-0 text-white text-[11px] font-bold uppercase tracking-[0.18em] py-1.5 px-3 flex items-center gap-1.5"
        style={{ background: "var(--ups-accent)" }}
      >
        <Percent size={12} /> {discount}% Partner Discount
      </div>

      {isCustom && (
        <div
          className="mb-4 p-3 flex items-start gap-3 -mt-1"
          style={{ background: "#FFB500", border: "2px solid var(--ups-ink)" }}
          data-testid="custom-quote-banner"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em]">
              {isCombined ? "Combined Quote contains custom items" : "Custom Quote · Approximate"}
            </div>
            <div className="text-[13px] mt-1 leading-snug">
              {quote.custom_note ||
                "This is a custom configuration. The pricing below is approximate — our Print Manager will review and confirm."}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start gap-3 mb-4 pr-28">
        <div
          className="w-9 h-9 flex items-center justify-center shrink-0 mt-0.5"
          style={{ background: "#FFB500", color: "#1C1917" }}
        >
          <Tag size={18} strokeWidth={2.5} />
        </div>
        <div>
          <div className="overline mb-1">{isCombined ? "Combined Quote" : "Quote"}</div>
          <h3 className="font-display text-2xl font-bold leading-tight" data-testid="quote-product-name">
            {isCombined ? `${lines.length} Items` : (quote.product_name || "Custom Print Quote")}
          </h3>
          {!isCombined && quote.specifications && (
            <p className="text-sm text-[color:var(--ups-subtext)] mt-1">{quote.specifications}</p>
          )}
          {isCombined && (
            <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
              One order, one email, one grand total.
            </p>
          )}
        </div>
      </div>

      {isBigHeads && sessionId && bhQty > 0 && (
        <BigHeadsPanel
          sessionId={sessionId}
          quantity={bhQty}
          fatheads={bhList}
          onChange={onBigheadsChange || (() => {})}
        />
      )}

      {isCombined && (
        <LineItemsTable
          lines={lines}
          onRequote={!disabled && onRequote ? onRequote : null}
          productsById={productsById}
          papersById={papersById}
        />
      )}

      {!isCombined && quote.calculation && (
        <div className="mb-5">
          <div className="overline mb-2">Calculation</div>
          <pre
            className="font-mono text-[13px] leading-relaxed whitespace-pre-wrap p-3 rounded-sm"
            style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
          >
            {quote.calculation}
          </pre>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div className="p-4" style={{ border: "1px dashed var(--ups-border)" }}>
          <div className="overline">{isCombined ? "Combined Retail" : "Retail Price"}</div>
          <div
            className="mt-1 text-2xl font-display font-bold"
            style={{ color: "var(--ups-ink)" }}
            data-testid="retail-price"
          >
            {formatUSD(retail)}
          </div>
        </div>
        <div
          className="p-4 relative"
          style={{ border: "2px solid var(--ups-ink)", background: "var(--ups-muted)" }}
        >
          <div className="overline" style={{ color: "var(--ups-ink)" }}>{isCombined ? "Combined Your Price" : "Your Price"}</div>
          <div
            className="mt-1 text-4xl font-display font-black tracking-tight"
            style={{ color: "var(--ups-accent)" }}
            data-testid="ups-price"
          >
            {formatUSD(ups)}
          </div>
          <div className="text-xs mt-1 font-mono" style={{ color: "var(--ups-subtext)" }}>
            Your Margin: {formatUSD(savings)} ({discount}%)
          </div>
        </div>
      </div>

      {quote.bulk_pricing_available && (
        <a
          href="tel:+17708752525"
          className="mb-5 px-3 py-2.5 rounded-sm flex items-center gap-3 text-[12px] transition hover:-translate-y-0.5"
          style={{
            background: "#FFFBEB",
            border: "2px solid var(--ups-ink)",
            boxShadow: "3px 3px 0 var(--ups-ink)",
            color: "var(--ups-ink)",
            textDecoration: "none",
          }}
          data-testid="bulk-pricing-badge"
        >
          <span
            className="w-7 h-7 flex items-center justify-center shrink-0"
            style={{ background: "#FFB500", color: "var(--ups-ink)" }}
          >
            <Phone size={14} strokeWidth={2.5} />
          </span>
          <span className="flex-1 leading-snug">
            <span className="font-bold uppercase tracking-[0.16em] text-[10px]" style={{ color: "#92400E" }}>
              Bulk pricing available
            </span>
            <br />
            For orders of 1000+, contact the store directly to unlock bulk rates beyond what you see here.
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em] shrink-0"
            style={{ color: "var(--ups-accent)" }}
          >
            Call →
          </span>
        </a>
      )}

      {upsells.length > 0 && (
        <div className="mb-5" data-testid="upsell-section">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">{isCombined ? "Upsells (highest-margin item)" : "Upsell Options"}</div>
            <span className="text-[11px] font-mono text-[color:var(--ups-subtext)]">suggest these to increase ticket size</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {upsells.map((u, idx) => (
              <UpsellCard
                key={idx}
                item={u}
                index={idx}
                onClick={() => onPickUpsell && onPickUpsell(u)}
              />
            ))}
          </div>
        </div>
      )}

      {!isCombined && (quote.quantity || quote.unit_type || quote.turnaround) && (
        <div className="flex flex-wrap gap-2 mb-5 text-xs font-mono">
          {quote.quantity && (
            <span className="px-2 py-1" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
              QTY: {quote.quantity} {quote.unit_type || ""}
            </span>
          )}
          {quote.turnaround && (
            <span className="px-2 py-1" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
              Turnaround: {quote.turnaround}
            </span>
          )}
        </div>
      )}

      {isBigHeads && !sessionId && (
        <div
          className="mt-5 mb-5 px-3 py-3 text-[12px] font-mono"
          style={{ background: "#FEE2E2", border: "1px solid #DC2626", color: "#991B1B" }}
        >
          Upload box can&apos;t load — session missing. Please refresh the page and try again.
        </div>
      )}

      <button
        onClick={onOrderNow}
        disabled={disabled || bhIncomplete}
        className="btn-brutal w-full py-3 px-5 rounded-sm flex items-center justify-center gap-2 text-base"
        data-testid="order-now-button"
      >
        <ShoppingCart size={18} />{" "}
        {isBigHeads && bhIncomplete
          ? `Upload ${bhQty - bhReady} more photo${bhQty - bhReady === 1 ? "" : "s"} to order`
          : isCustom
          ? "Submit Custom Quote Request"
          : isCombined
          ? `Order ${lines.length} Items`
          : "Order Now"}
      </button>
      {onEmailQuote && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="email-quote-actions">
          <button
            type="button"
            onClick={() => handleEmailClick("full")}
            disabled={emailingVariant !== null || sentVariants.full}
            className="py-2.5 px-3 rounded-sm flex items-center justify-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] transition"
            style={{
              background: sentVariants.full ? "var(--ups-muted)" : "#fff",
              border: "2px solid var(--ups-ink)",
              color: "var(--ups-ink)",
              boxShadow: sentVariants.full ? "none" : "3px 3px 0 var(--ups-ink)",
              opacity: emailingVariant === "full" ? 0.7 : 1,
            }}
            data-testid="email-quote-full-button"
          >
            {emailingVariant === "full" ? (
              <><Loader2 size={13} className="animate-spin" /> Sending…</>
            ) : sentVariants.full ? (
              <><Mail size={13} /> Quote sent</>
            ) : (
              <><Mail size={13} /> Email a Quote</>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleEmailClick("retail")}
            disabled={emailingVariant !== null || sentVariants.retail}
            className="py-2.5 px-3 rounded-sm flex items-center justify-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] transition"
            style={{
              background: sentVariants.retail ? "var(--ups-muted)" : "#FFB500",
              border: "2px solid var(--ups-ink)",
              color: "var(--ups-ink)",
              boxShadow: sentVariants.retail ? "none" : "3px 3px 0 var(--ups-ink)",
              opacity: emailingVariant === "retail" ? 0.7 : 1,
            }}
            data-testid="email-quote-retail-button"
          >
            {emailingVariant === "retail" ? (
              <><Loader2 size={13} className="animate-spin" /> Sending…</>
            ) : sentVariants.retail ? (
              <><Mail size={13} /> Retail quote sent</>
            ) : (
              <><Send size={13} /> Email a Retail Quote</>
            )}
          </button>
          <button
            type="button"
            onClick={handleCopyClick}
            className="py-2.5 px-3 rounded-sm flex items-center justify-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] transition"
            style={{
              background: copied ? "var(--ups-muted)" : "#fff",
              border: "2px solid var(--ups-ink)",
              color: "var(--ups-ink)",
              boxShadow: copied ? "none" : "3px 3px 0 var(--ups-ink)",
            }}
            data-testid="copy-quote-button"
            title="Copy a paste-friendly summary to your clipboard"
          >
            {copied ? (
              <><Check size={13} /> Copied</>
            ) : (
              <><Copy size={13} /> Copy Quote</>
            )}
          </button>
          <p
            className="sm:col-span-3 -mt-0.5 text-[11px] font-mono leading-snug"
            style={{ color: "var(--ups-subtext)" }}
            data-testid="email-retail-hint"
          >
            <span className="font-bold">Email a Retail Quote</span> = customer-safe (no internal discounted price). <span className="font-bold">Copy Quote</span> = paste into Slack, SMS, or email.
          </p>
        </div>
      )}
      <p className="mt-3 text-[11px] font-mono text-center" style={{ color: "var(--ups-subtext)" }}>
        {isCustom
          ? "Print Manager will follow up with exact pricing after review."
          : "Attach your artwork on the next step. We'll email you a confirmation once submitted."}
      </p>
    </div>
  );
};

export default PricingQuoteCard;
